import { ModButton } from '@moderneinc/design-system-components';
import {
  IconButton,
  type IconButtonProps,
  type SxProps,
  type Theme,
  Tooltip
} from '@mui/material';
import type {
  FunctionComponent,
  MouseEventHandler,
  ReactNode,
  TouchEvent
} from 'react';

import { CollapseIcon, ExpandIcon } from '../../../icons/icons';

type ExpandCollapseProps = {
  variant?: 'icon' | 'iconWithText';
  disableRipple?: boolean;
  expandText?: string;
  collapseText?: string;
  collapseIcon?: ReactNode;
  expandIcon?: ReactNode;
  iconPosition?: 'start' | 'end';
  isExpanded: boolean;
  color?: IconButtonProps['color'];
  size?: IconButtonProps['size'];
  onClick: MouseEventHandler<Element>;
  onTouchStart?: (event: TouchEvent<Element>) => void;
  'aria-controls'?: string;
  'aria-describedby'?: string;
  'aria-haspopup'?: boolean;
  title?: string;
  sx?: SxProps<Theme>;
};

export const ExpandCollapse: FunctionComponent<ExpandCollapseProps> = ({
  variant = 'icon',
  expandText = 'Show',
  collapseText = 'Hide',
  collapseIcon = <CollapseIcon />,
  expandIcon = <ExpandIcon />,
  iconPosition = 'start',
  isExpanded,
  color = 'secondary',
  size = 'small',
  onClick,
  title,
  ...rest
}) => {
  const testId = `${isExpanded ? 'collapse' : 'expand'}-button`;
  const icon = isExpanded ? collapseIcon : expandIcon;
  if (variant === 'iconWithText') {
    return (
      <ModButton
        data-testid={testId}
        variant="tertiary"
        startIcon={iconPosition === 'start' ? icon : undefined}
        endIcon={iconPosition === 'end' ? icon : undefined}
        onClick={onClick}
        size={size === 'large' ? 'medium' : size}
        title={title}
        {...rest}
      >
        {isExpanded ? collapseText : expandText}
      </ModButton>
    );
  } else {
    return (
      <Tooltip placement="bottom" title={title}>
        <IconButton
          data-testid={testId}
          color={color}
          onClick={onClick}
          size={size}
          {...rest}
        >
          {isExpanded ? collapseIcon : expandIcon}
        </IconButton>
      </Tooltip>
    );
  }
};
